import React from "react";
import {
  ActivityIndicator,
  FlatList,
  NativeSyntheticEvent,
  Pressable,
  PressableProps,
  StyleProp,
  StyleSheet,
  Text,
  TextInput,
  TextInputKeyPressEventData,
  TextInputProps,
  TextStyle,
  View,
  ViewStyle,
} from "react-native";

import { kColors } from "src/kColors";
import { kFonts } from "src/kFonts";
import { kStyleConst } from "src/kStyle";
import { useOnClickOutside } from "usehooks-ts";

import { PdChevronDown, PdChevronUp, PdClose } from "../Icons";
import { PdTag, PdTagLabel } from "../layout";
import { tPressableState } from "../types";
import { mergeRefs } from "../utils";
import { PdIconButton } from "./icon-button";

export type tPdSelectOption = {
  value: string;
  label: string;
};

export type tPdSelect = {
  value?: tPdSelectOption | null;
  onChange?: (option: tPdSelectOption | null) => void;
  options: tPdSelectOption[];
  valueExtractor?: (option?: tPdSelectOption | null) => string;
  labelExtractor?: (option?: tPdSelectOption | null) => string;
  direction?: "bottom" | "top";
  isDisabled?: boolean;
  isClearable?: boolean;
  isLoading?: boolean;
  containerStyle?: StyleProp<ViewStyle>;
} & Omit<TextInputProps, "value" | "onChange">;

export type tPdSelectMultiple = {
  value?: tPdSelectOption[] | null;
  onChange?: (option: tPdSelectOption[]) => void;
  style?: StyleProp<ViewStyle>;
  selectStyle?: StyleProp<TextStyle>;
} & Omit<tPdSelect, "style" | "value" | "onChange">;

const EmptyMenuItem = () => (
  <View style={styles.emptyMenuItem}>
    <Text numberOfLines={1} style={styles.emptyMenuItemLabel}>
      <Text>No results</Text>
    </Text>
  </View>
);

const PdMenuItem = (
  props: {
    value?: tPdSelectOption | null;
    item: tPdSelectOption;
    searchTerm: string;
    labelExtractor?: (option: tPdSelectOption) => string;
  } & PressableProps,
) => {
  const { value, item, searchTerm, labelExtractor, ...rest } = props;
  const menuItemRef = React.useRef<View>(null);

  const label = item?.label || labelExtractor?.(item) || "";
  const labelIsSelected = value?.label === label;
  const aHighlightIndex = label.toLowerCase().indexOf(searchTerm.toLowerCase());
  const startText = label.substring(0, aHighlightIndex);
  const highlightedText = label.substr(aHighlightIndex, searchTerm.length);
  const endText = label.substring(aHighlightIndex + searchTerm.length);

  return (
    <Pressable ref={menuItemRef} accessibilityRole="menuitem" {...rest}>
      {({ hovered, focused }: tPressableState) => (
        <View style={[hovered || focused || labelIsSelected ? styles.selectedMenuItem : {}, styles.menuItem]}>
          <Text numberOfLines={1} style={[styles.menuItemLabel, labelIsSelected ? styles.selectedMenuItemLabel : {}]}>
            {startText}
            <Text style={styles.menuItemLabelHighlight}>{highlightedText}</Text>
            {endText}
          </Text>
        </View>
      )}
    </Pressable>
  );
};

export const PdSelect = React.forwardRef((props: tPdSelect, ref: React.Ref<TextInput>) => {
  const {
    value,
    onChange,
    options,
    valueExtractor,
    labelExtractor,
    direction = "bottom",
    isDisabled = false,
    isClearable = true,
    isLoading = false,
    placeholder,
    containerStyle,
    style,
    ...textInputProps
  } = props;

  const [searchTerm, setSearchTerm] = React.useState("");
  const [isOpen, setOpen] = React.useState(false);
  const containerRef = React.useRef<View>(null);
  const textInputRef = React.useRef<TextInput>(null);

  const optionsFiltered = React.useMemo(
    () =>
      options.filter((option) => {
        const label = option?.label || labelExtractor?.(option) || "";
        return label.toLowerCase().includes(searchTerm.toLowerCase());
      }),
    [searchTerm, labelExtractor, options],
  );

  const handleOnChangeTextInput = (newValue: string) => {
    setSearchTerm(newValue);
  };

  const handleOnPressTextInput = () => {
    if (isDisabled) return;
    textInputRef.current?.focus();
    setOpen(true);
  };

  const handleOnPressMenuItem = (item: tPdSelectOption) => {
    textInputRef.current?.focus();
    onChange?.(item);
    setSearchTerm("");
    setOpen(false);
  };

  const ChevronIcon = isOpen ? PdChevronUp : PdChevronDown;
  const controlledValue = value?.label || labelExtractor?.(value);

  const handleOnKeyPress = (event: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
    if (isDisabled) return;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const eventCode = event.code;
    if (eventCode === "Enter" || (eventCode === "ArrowDown" && !isOpen)) {
      setOpen(true);
    } else if (eventCode === "Escape") {
      setOpen(false);
    }
  };

  const handleClickOutside = () => {
    setOpen(false);
  };

  const handleOnClear = () => {
    onChange?.(null);
  };

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  useOnClickOutside(containerRef, handleClickOutside);

  return (
    <View
      ref={containerRef}
      style={[styles.select, containerStyle, isDisabled || isLoading ? styles.selectDisabled : {}]}
    >
      <Pressable
        disabled={isDisabled || isLoading}
        focusable={false}
        onPress={handleOnPressTextInput}
        style={styles.selectButtonInput}
      >
        <TextInput
          ref={mergeRefs([ref, textInputRef])}
          focusable
          value={searchTerm}
          placeholder={controlledValue || placeholder}
          placeholderTextColor={controlledValue ? kColors.brandDark : undefined}
          onChangeText={handleOnChangeTextInput}
          onKeyPress={handleOnKeyPress}
          style={[styles.selectInput, style]}
          editable={!isDisabled && !isLoading}
          {...textInputProps}
        />
        {!isLoading && isClearable && value && (
          <Pressable onPress={handleOnClear} focusable={false} style={styles.selectButton}>
            <PdClose focusable={false} />
          </Pressable>
        )}
        {isLoading && (
          <View style={styles.selectButton}>
            <ActivityIndicator size="small" color={kColors.brandDarkGreen} />
          </View>
        )}
        <View focusable={false} style={styles.selectClearableButtonIcon}>
          <ChevronIcon focusable={false} />
        </View>
      </Pressable>
      {!!(isOpen || searchTerm) && (
        <FlatList
          style={[
            styles.selectMenuList,
            direction === "bottom" ? styles.selectMenuListBottom : {},
            direction === "top" ? styles.selectMenuListTop : {},
          ]}
          data={optionsFiltered}
          renderItem={({ item }) => (
            <PdMenuItem
              value={value}
              item={item}
              searchTerm={searchTerm}
              labelExtractor={labelExtractor}
              onPress={() => {
                handleOnPressMenuItem?.(item);
              }}
            />
          )}
          ListEmptyComponent={EmptyMenuItem}
          keyExtractor={(item) => item?.value || (valueExtractor?.(item) ?? "")}
        />
      )}
    </View>
  );
});

export const PdSelectMultiple = React.forwardRef((props: tPdSelectMultiple, ref: React.Ref<TextInput>) => {
  const { value, onChange, style, selectStyle, ...rest } = props;

  const safeValue = value || [];

  const handleOnChange: tPdSelect["onChange"] = (option) => {
    if (!option) return;
    const maybeAddedOption = safeValue.find((item) => item.value === option.value);
    if (maybeAddedOption) return;
    onChange?.([...safeValue, option]);
  };

  const handleOnRemove = (option: tPdSelectOption) => {
    onChange?.(safeValue.filter((item) => item.value !== option.value));
  };

  return (
    <View style={style}>
      <PdSelect
        containerStyle={styles.pdSelectMultiple}
        ref={ref}
        style={selectStyle}
        onChange={handleOnChange}
        isClearable={false}
        placeholder="Select items..."
        {...rest}
      />
      {(value?.length ?? 0) > 0 && (
        <View style={styles.pdSelectMultipleItemContainer}>
          {value?.map((item) => (
            <PdTag style={styles.pdSelectMultipleItem} key={item.value}>
              <PdTagLabel>{item.label}</PdTagLabel>
              <PdIconButton
                size="sm"
                onPress={() => handleOnRemove(item)}
                contentContainerStyle={styles.pdSelectMultipleRemoveIconButton}
                icon={<PdClose style={styles.pdSelectMultipleRemoveIcon} />}
              />
            </PdTag>
          ))}
        </View>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  emptyMenuItem: {
    paddingHorizontal: 2 * kStyleConst.kSpacing,
    paddingVertical: kStyleConst.kSpacing,
  },
  emptyMenuItemLabel: {
    ...kFonts.body,
    color: kColors.brandDark,
    lineHeight: 25,
  },
  menuItem: {
    paddingHorizontal: 2 * kStyleConst.kSpacing,
    paddingVertical: kStyleConst.kSpacing,
  },
  menuItemLabel: {
    ...kFonts.body,
    color: kColors.brandDark,
    lineHeight: 25,
  },
  menuItemLabelHighlight: {
    fontWeight: "500",
  },
  pdSelectMultiple: {
    zIndex: 1,
  },
  pdSelectMultipleItem: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "center",
    margin: kStyleConst.kSpacing,
  },
  pdSelectMultipleItemContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    margin: -kStyleConst.kSpacing,
    marginTop: kStyleConst.kSpacing,
  },
  pdSelectMultipleRemoveIcon: {
    color: kColors.white,
    height: 10,
    width: 10,
  },
  pdSelectMultipleRemoveIconButton: {
    height: 10,
    marginLeft: kStyleConst.kSpacing,
    width: 10,
  },
  select: {
    borderColor: kColors.gray300,
    borderWidth: 1,
    paddingHorizontal: 2 * kStyleConst.kSpacing,
    paddingVertical: 1 * kStyleConst.kSpacing,
    position: "relative",
  },
  selectButton: {
    height: "100%",
    justifyContent: "center",
    marginHorizontal: kStyleConst.kSpacing,
    width: 12,
  },
  selectButtonInput: {
    alignItems: "center",
    flexDirection: "row",
  },
  selectClearableButtonIcon: {
    height: "100%",
    justifyContent: "center",
    marginLeft: kStyleConst.kSpacing,
    width: 12,
  },
  selectDisabled: {
    opacity: 0.4,
  },
  selectInput: {
    ...kFonts.body,
    color: kColors.brandDark,
    lineHeight: 25,
    width: "100%",
  },
  selectMenuList: {
    backgroundColor: kColors.white,
    left: 0,
    maxHeight: 150,
    overflowY: "auto",
    position: "absolute",
    right: 0,
    shadowColor: kColors.black,
    shadowOffset: { width: 5, height: 5 },
    shadowOpacity: 0.1,
    shadowRadius: 10,
  },
  selectMenuListBottom: {
    top: 45,
  },
  selectMenuListTop: {
    bottom: 45,
  },
  selectedMenuItem: {
    backgroundColor: kColors.gray50,
  },
  selectedMenuItemLabel: {
    fontWeight: "500",
  },
});
